import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/services/api";
import { leadEmailVerified } from "@/utils/tracking";

export const LeadVerifyPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("Verifying your email...");
  const [timer, setTimer] = useState(3);
  const hasVerifiedRef = useRef(false);

  // Token verification - runs only once when component mounts
  useEffect(() => {
    if (hasVerifiedRef.current) return; // Prevent multiple verifications
    hasVerifiedRef.current = true;

    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setMessage("Invalid verification link. No token provided.");
      return;
    }

    api.leads
      .verifyEmail(token)
      .then((response) => {
        setStatus("success");
        setMessage("Your email has been verified! Matching you with advisors...");
        setTimer(3);
        // Store leadId for redirect
        (window as any).leadId = response.leadId;

        // Track email verification
        leadEmailVerified({
          lead_id: response.leadId,
          time_to_verify_minutes: response.timeToVerifyMinutes,
        });
      })
      .catch((err: unknown) => {
        setStatus("error");
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to verify email. The link may have expired.";
        setMessage(errorMessage);
      });
  }, [searchParams]);

  // Handle countdown timer
  useEffect(() => {
    if (status === "success") {
      if (timer > 0) {
        const timeout = setTimeout(() => {
          setTimer((prev) => prev - 1);
        }, 1000);
        return () => clearTimeout(timeout);
      } else {
        // Redirect to matching page
        const leadId = (window as any).leadId;
        if (leadId) {
          navigate(`/leads/matching/${leadId}`);
        }
      }
    }
  }, [status, timer, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-theme-bg-page">
      <div className="bg-theme-bg-surface p-16 rounded-2xl shadow-md w-full max-w-md text-center">
        <h2 className="text-2xl font-bold mb-8 text-theme-text-heading">
          {status === "loading" && "Verifying..."}
          {status === "success" && "Email Verified!"}
          {status === "error" && "Verification Failed"}
        </h2>
        <p className="text-base text-theme-text">{message}</p>
        {status === "success" && (
          <p className="mt-8 text-sm text-theme-text-secondary">
            Redirecting in {timer} second{timer !== 1 ? "s" : ""}...
          </p>
        )}
        {status === "error" && (
          <p className="mt-8 text-sm text-theme-text-secondary">
            Please contact support or request a new verification email.
          </p>
        )}
      </div>
    </div>
  );
};
